import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  Grid,
  Tooltip,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { prepareFileForSubmission } from '../utils/fileHandler';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  SmartToy as ModelIcon,
  DragHandle as DragIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isVideoModel as checkIsVideoModel } from '../config/modelConfig';

interface AIFunction {
  id: string;
  name: string;
  display_name?: string;
  description?: string;
  input_types: string[];
  allowed_models?: string[];
  metadata?: {
    knowledge_categories?: string[];
    knowledge_chunks?: number;
    [key: string]: any;
  };
}

interface BatchInput {
  id: string;
  type: 'text' | 'file';
  value: string;
  fileName?: string;
  fileObject?: File;
  previewUrl?: string;
}

interface BatchResult {
  inputId: string;
  status: 'pending' | 'running' | 'success' | 'error';
  result?: any;
  error?: string;
}

// Configurable parallel execution pool size
const PARALLEL_POOL_SIZE = 5;

export const ToolBatchRunner: React.FC = () => {
  const { functionId } = useParams<{ functionId: string }>();
  const navigate = useNavigate();
  const { user, getEffectiveCustomerTag } = useAuth();
  
  const [func, setFunc] = useState<AIFunction | null>(null);
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState<BatchInput[]>([]);
  const [results, setResults] = useState<BatchResult[]>([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [knowledgeChunks, setKnowledgeChunks] = useState(2); // Default to 2 as requested
  const [selectedModel, setSelectedModel] = useState<string>(''); // Selected AI model
  const [resultsHeight, setResultsHeight] = useState(500); // Resizable results panel height
  const [isDragging, setIsDragging] = useState(false);
  
  // Video generation settings
  const [videoDuration, setVideoDuration] = useState(6); // 4-8 seconds for video models (default 6 for better quality)
  const [videoAspectRatio, setVideoAspectRatio] = useState('16:9');
  
  // Check if selected model is a video generation model
  const isVideoModel = selectedModel ? checkIsVideoModel(selectedModel) : false;

  useEffect(() => {
    const loadFunction = async () => {
      if (!functionId) return;
      
      setLoading(true);
      try {
        const idToken = await user?.getIdToken();
        const response = await fetch(`/api/v2/ai-functions/${functionId}`, {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const functionData = data.data || data;
          setFunc(functionData);
          
          // Set knowledge chunks from function metadata if available
          if (functionData.metadata?.knowledge_chunks !== undefined) {
            setKnowledgeChunks(functionData.metadata.knowledge_chunks);
          }
          
          // Set default model to first allowed model
          if (functionData.allowed_models && functionData.allowed_models.length > 0) {
            setSelectedModel(functionData.allowed_models[0]);
          }
        }
      } catch (error) {
        console.error('Failed to load function:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFunction();
  }, [functionId, user]);

  const supportsText = func?.input_types.includes('text');
  const supportsFiles = func?.input_types.some(type => ['image', 'pdf', 'audio', 'video', 'binary'].includes(type));
  const fileAccept = React.useMemo(() => {
    if (!supportsFiles) return undefined;
    if (func?.input_types.includes('binary')) return undefined;

    const acceptTypes: string[] = [];
    if (func?.input_types.includes('image')) acceptTypes.push('image/*');
    if (func?.input_types.includes('pdf')) acceptTypes.push('.pdf');
    if (func?.input_types.includes('audio')) acceptTypes.push('audio/*', '.mp3');
    if (func?.input_types.includes('video')) acceptTypes.push('video/*', '.mp4');

    return acceptTypes.length ? acceptTypes.join(',') : undefined;
  }, [supportsFiles, func?.input_types]);

  const addTextInput = () => {
    setInputs([...inputs, {
      id: `text-${Date.now()}`,
      type: 'text',
      value: ''
    }]);
  };

  const addFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const timestamp = Date.now();
    const newInputs = files.map((file, index) => ({
      id: `file-${timestamp}-${index}-${file.name}`,
      type: 'file' as const,
      value: '',
      fileName: file.name,
      fileObject: file,
      previewUrl: URL.createObjectURL(file)
    }));
    setInputs(prev => [...prev, ...newInputs]);
    event.target.value = '';
  };

  const updateTextInput = (id: string, value: string) => {
    setInputs(inputs.map(input =>
      input.id === id ? { ...input, value } : input
    ));
  };

  const removeInput = (id: string) => {
    setInputs(prev => {
      const target = prev.find(input => input.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter(input => input.id !== id);
    });
  };

  const runBatch = async () => {
    if (inputs.length === 0) return;
    
    setRunning(true);
    setProgress(0);
    
    const newResults: BatchResult[] = inputs.map(input => ({
      inputId: input.id,
      status: 'pending'
    }));
    setResults(newResults);

    let completed = 0;
    const totalInputs = inputs.length;

    // Process function for a single input
    const processInput = async (input: BatchInput) => {
      // Update status to running
      setResults(prev => prev.map(r =>
        r.inputId === input.id ? { ...r, status: 'running' } : r
      ));

      try {
        const idToken = await user?.getIdToken();
        const endpoint = `/api/v2/functions/${getEffectiveCustomerTag()}/${func?.name}`;
        
        let body: any;
        const headers: any = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        };
        
        // Add model header if a specific model is selected
        if (selectedModel) {
          headers['EASYAI-MODEL'] = selectedModel;
        }

        if (input.type === 'text') {
          const requestBody: any = {
            input: input.value,
            input_type: 'text',
          };
          
          // Add video generation metadata if using a video model
          if (isVideoModel) {
            requestBody.metadata = {
              ...(requestBody.metadata || {}),
              duration: videoDuration,
              aspect_ratio: videoAspectRatio
            };
          }
          
          // Add vector search metadata if function has knowledge categories
          if (func?.metadata?.knowledge_categories && func.metadata.knowledge_categories.length > 0 && knowledgeChunks > 0) {
            requestBody.metadata = {
              ...(requestBody.metadata || {}),
              vector_search: {
                enabled: true,
                category_ids: func.metadata.knowledge_categories,
                top_k: knowledgeChunks
              }
            };
          }
          
          body = JSON.stringify(requestBody);
        } else {
          // For files, use unified file handler
          if (!input.fileObject) {
            throw new Error('File data missing for input');
          }
          
          // Use unified file handler (automatically handles GCS upload for large files)
          const idToken = await user?.getIdToken();
          const prepared = await prepareFileForSubmission(input.fileObject, undefined, idToken);
          
          const requestBody: any = {
            input: prepared.input,
            input_type: prepared.input_type,
            metadata: prepared.metadata || {}
          };
          
          // Add video generation metadata if using a video model
          if (isVideoModel) {
            requestBody.metadata.duration = videoDuration;
            requestBody.metadata.aspect_ratio = videoAspectRatio;
          }
          
          // Add vector search metadata if function has knowledge categories
          if (func?.metadata?.knowledge_categories && func.metadata.knowledge_categories.length > 0 && knowledgeChunks > 0) {
            requestBody.metadata.vector_search = {
              enabled: true,
              category_ids: func.metadata.knowledge_categories,
              top_k: knowledgeChunks
            };
          }
          
          body = JSON.stringify(requestBody);
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body
        });

        if (response.ok) {
          const data = await response.json();
          
          // Handle both wrapped { result: ... } and direct response formats
          let result = data?.result;
          if (result === null || result === undefined) {
            // If no result key, the entire response is the result
            // (e.g., direct array or object response)
            result = data;
          }
          
          // Check if we have a valid result (not null/undefined, and not empty object/array)
          const hasResult = result !== null && 
                           result !== undefined && 
                           !(Array.isArray(result) && result.length === 0) &&
                           !(typeof result === 'object' && !Array.isArray(result) && Object.keys(result).length === 0);

          if (hasResult) {
            setResults(prev => prev.map(r =>
              r.inputId === input.id
                ? { ...r, status: 'success', result }
                : r
            ));
          } else {
            setResults(prev => prev.map(r =>
              r.inputId === input.id
                ? { ...r, status: 'error', result: {}, error: 'Empty result returned' }
                : r
            ));
          }
        } else {
          const errorText = await response.text();
          setResults(prev => prev.map(r =>
            r.inputId === input.id
              ? { ...r, status: 'error', error: errorText }
              : r
          ));
        }
      } catch (error) {
        setResults(prev => prev.map(r =>
          r.inputId === input.id
            ? { ...r, status: 'error', error: String(error) }
            : r
        ));
      }

      completed++;
      setProgress((completed / totalInputs) * 100);
    };

    // Run inputs in parallel batches
    for (let i = 0; i < inputs.length; i += PARALLEL_POOL_SIZE) {
      const batch = inputs.slice(i, i + PARALLEL_POOL_SIZE);
      await Promise.all(batch.map(input => processInput(input)));
    }

    setRunning(false);
  };

  const exportResults = () => {
    const exportData = inputs.map((input, idx) => {
      const result = results[idx];
      return {
        input: input.type === 'text' ? input.value : input.fileName,
        status: result?.status,
        result: result?.result,
        error: result?.error
      };
    });

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `batch-results-${func?.name}-${Date.now()}.json`;
    link.click();
  };

  // Resizable panel drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      // Calculate new height based on mouse position
      const resultsCard = document.getElementById('results-card');
      if (!resultsCard) return;
      
      const cardRect = resultsCard.getBoundingClientRect();
      const newHeight = Math.max(200, Math.min(1000, e.clientY - cardRect.top + 20));
      setResultsHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!func) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">Function not found</Typography>
        <Button onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {func.display_name || func.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {func.description || 'Batch run this tool with multiple inputs'}
          </Typography>
        </Box>
      </Box>

      {/* Model Selector - Enhanced UX */}
      {func.allowed_models && func.allowed_models.length > 0 && (
        <Card 
          sx={{ 
            mb: 3, 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
            border: '2px solid',
            borderColor: 'rgba(102, 126, 234, 0.3)',
            boxShadow: 2
          }}
        >
          <CardContent sx={{ py: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ModelIcon sx={{ color: '#667eea', mr: 1, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea' }}>
                AI Model Selection
              </Typography>
            </Box>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <FormControl 
                  fullWidth 
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'white',
                      '&:hover': {
                        boxShadow: 1
                      }
                    }
                  }}
                >
                  <InputLabel>Select Model</InputLabel>
                  <Select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    label="Select Model"
                    disabled={running}
                  >
                    {func.allowed_models.map((model: string) => (
                      <MenuItem key={model} value={model}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ModelIcon sx={{ fontSize: 18, color: '#667eea' }} />
                          <Typography>{model}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={7}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    All {inputs.length || 0} input{inputs.length !== 1 ? 's' : ''} will use <strong>{selectedModel || 'the default model'}</strong>
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Video Generation Settings */}
      {isVideoModel && (
        <Card 
          sx={{ 
            mb: 3, 
            background: 'linear-gradient(135deg, rgba(255, 167, 38, 0.08) 0%, rgba(255, 87, 34, 0.08) 100%)',
            border: '2px solid',
            borderColor: 'rgba(255, 167, 38, 0.3)',
            boxShadow: 2
          }}
        >
          <CardContent sx={{ py: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#ff9800' }}>
                🎥 Video Generation Settings
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="medium">
                  <InputLabel>Duration (seconds)</InputLabel>
                  <Select
                    value={videoDuration}
                    onChange={(e) => setVideoDuration(Number(e.target.value))}
                    label="Duration (seconds)"
                    disabled={running}
                  >
                    <MenuItem value={4}>4 seconds</MenuItem>
                    <MenuItem value={5}>5 seconds</MenuItem>
                    <MenuItem value={6}>6 seconds</MenuItem>
                    <MenuItem value={7}>7 seconds</MenuItem>
                    <MenuItem value={8}>8 seconds</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="medium">
                  <InputLabel>Aspect Ratio</InputLabel>
                  <Select
                    value={videoAspectRatio}
                    onChange={(e) => setVideoAspectRatio(e.target.value)}
                    label="Aspect Ratio"
                    disabled={running}
                  >
                    <MenuItem value="16:9">16:9 (Landscape)</MenuItem>
                    <MenuItem value="9:16">9:16 (Portrait)</MenuItem>
                    <MenuItem value="1:1">1:1 (Square)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Alert severity="warning" sx={{ py: 0.5, height: '100%', display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">
                    Video generation takes 1-5 minutes per video
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Left: Inputs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                📝 Inputs
              </Typography>

              {/* Knowledge Chunks Control */}
              {func?.metadata?.knowledge_categories && func.metadata.knowledge_categories.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    📚 Knowledge Base Settings
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Number of knowledge chunks to retrieve: {knowledgeChunks}
                  </Typography>
                  <Slider
                    value={knowledgeChunks}
                    onChange={(_, value) => setKnowledgeChunks(value as number)}
                    min={0}
                    max={20}
                    step={1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                      { value: 15, label: '15' },
                      { value: 20, label: '20' },
                    ]}
                    valueLabelDisplay="auto"
                    disabled={running}
                    sx={{ 
                      color: '#667eea',
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#667eea',
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: '#667eea',
                      },
                    }}
                  />
                </Box>
              )}

              {inputs.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Add text inputs or upload files to batch process
                </Alert>
              )}

              {/* Input List */}
              <Box sx={{ mb: 2, maxHeight: 400, overflowY: 'auto' }}>
                {inputs.map((input, idx) => (
                  <Paper
                    key={input.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 1, position: 'relative' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip label={`#${idx + 1}`} size="small" sx={{ mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        {input.type === 'text' ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            value={input.value}
                            onChange={(e) => updateTextInput(input.id, e.target.value)}
                            placeholder="Enter text input..."
                            size="small"
                          />
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              📎 {input.fileName}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Result indicator */}
                        {results.find(r => r.inputId === input.id)?.status && (
                          <Chip
                            label={results.find(r => r.inputId === input.id)?.status}
                            size="small"
                            color={
                              results.find(r => r.inputId === input.id)?.status === 'success'
                                ? 'success'
                                : results.find(r => r.inputId === input.id)?.status === 'error'
                                ? 'error'
                                : 'default'
                            }
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => removeInput(input.id)}
                        disabled={running}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Add Input Buttons */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {supportsText && (
                  <Button
                    variant="outlined"
                    onClick={addTextInput}
                    disabled={running}
                    size="small"
                  >
                    + Add Text Input
                  </Button>
                )}
                {supportsFiles && (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={running}
                    size="small"
                  >
                    Upload Files
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={addFileInput}
                      accept={fileAccept}
                    />
                  </Button>
                )}
              </Box>

              {/* Run Button */}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<PlayIcon />}
                  onClick={runBatch}
                  disabled={inputs.length === 0 || running}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {running ? `Running (${PARALLEL_POOL_SIZE} at a time)...` : `Run ${inputs.length} Input${inputs.length !== 1 ? 's' : ''}`}
                </Button>
                
                {inputs.length > PARALLEL_POOL_SIZE && !running && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    Will process {PARALLEL_POOL_SIZE} items in parallel, then pick up more as they complete
                  </Typography>
                )}
                
                {running && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress variant="determinate" value={progress} />
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                      {Math.round(progress)}% complete
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Results - Resizable */}
        <Grid item xs={12} md={6}>
          <Card 
            id="results-card"
            sx={{ 
              position: 'relative',
              height: resultsHeight,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              pb: 1
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  📊 Results
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {results.length > 0 && (
                    <Chip 
                      label={`${results.filter(r => r.status === 'success').length}/${results.length} Success`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {results.length > 0 && (
                    <Tooltip title="Export results as JSON">
                      <IconButton onClick={exportResults} size="small">
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {results.length === 0 ? (
                <Alert severity="info">
                  Results will appear here after running your inputs
                </Alert>
              ) : (
                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                  {results.map((result, idx) => {
                    const input = inputs[idx];
                    return (
                      <Paper
                        key={result.inputId}
                        variant="outlined"
                        sx={{
                          p: 2,
                          mb: 1,
                          borderLeft: '4px solid',
                          borderLeftColor: result.status === 'success'
                            ? 'success.main'
                            : result.status === 'error'
                            ? 'error.main'
                            : result.status === 'running'
                            ? 'primary.main'
                            : 'grey.300',
                          bgcolor: result.status === 'running' 
                            ? 'rgba(25, 118, 210, 0.04)' 
                            : 'white'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Chip label={`#${idx + 1}`} size="small" sx={{ mr: 1 }} />
                          <Chip
                            label={result.status}
                            size="small"
                            color={
                              result.status === 'success'
                                ? 'success'
                                : result.status === 'error'
                                ? 'error'
                                : result.status === 'running'
                                ? 'primary'
                                : 'default'
                            }
                          />
                        </Box>
                        
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                          Input: {input?.type === 'text' ? input.value.substring(0, 50) + '...' : input?.fileName}
                        </Typography>

                        {result.status === 'success' && result.result && (
                          <Paper sx={{ p: 1.5, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.75rem', maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0' }}>
                            {(() => {
                              // Check if this is an image generation response
                              if (result.result && typeof result.result === 'object' && 'images' in result.result) {
                                const images = result.result.images;
                                if (Array.isArray(images) && images.length > 0) {
                                  return (
                                    <Box>
                                      {images.map((img: any, imgIdx: number) => (
                                        <Box key={imgIdx} sx={{ mb: 1 }}>
                                          {img.url && (
                                            <img 
                                              src={img.url} 
                                              alt={`Generated output ${imgIdx + 1}`}
                                              style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
                                            />
                                          )}
                                          {img.data && (
                                            <img 
                                              src={img.data} 
                                              alt={`Generated output ${imgIdx + 1}`}
                                              style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
                                            />
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                }
                              }
                              
                              // Otherwise display as JSON
                              return (
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                  {JSON.stringify(result.result, null, 2)}
                                </pre>
                              );
                            })()}
                          </Paper>
                        )}

                        {result.status === 'error' && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {result.error || 'Unknown error'}
                          </Alert>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </CardContent>
            
            {/* Resize Handle */}
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                height: 8,
                cursor: 'ns-resize',
                background: isDragging 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(to right, #e0e0e0 0%, #f5f5f5 50%, #e0e0e0 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
                borderTop: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)',
                }
              }}
            >
              <DragIcon 
                sx={{ 
                  fontSize: 16, 
                  color: isDragging ? 'white' : 'grey.500',
                  transform: 'rotate(90deg)'
                }} 
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ToolBatchRunner;

